import { useQueryParams } from 'hooks';
import React, { useEffect, useState } from 'react';
import { RestClient } from '../services/clients/RestClient';
import { AuthService } from '../services/users/AuthService';
import { UserService } from '../services/users/UserService';
import { ISessionData } from '../services/users/UserService.interface';

const restClient = new RestClient({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

const userService = new UserService(restClient, new AuthService());

type SessionState = {
  isSessionExpire: boolean;
  session: ISessionData | null;
};

type User = {
  userId: string;
  username: string;
  firstname: string;
  lastname: string;
  phone: string;
};

type Project = {
  projectId: string;
  projectName: string;
};

type Company = {
  companyId: string;
  companyName: string;
};

type Role = {
  roleId: string;
  roleName: string;
  permissions: string[];
};

type UserState = User & Project & Company & Role;

type SessionContextProps = (SessionState & UserState) | undefined;

const initialSessionState = {
  isSessionExpire: true,
  session: null,
  userState: null,
};

const initialSessionValue = {
  userId: '',
  username: '',
  firstname: '',
  lastname: '',
  phone: '',
  projectId: '',
  projectName: '',
  companyId: '',
  companyName: '',
  roleId: '',
  roleName: '',
  permissions: [],
};

const SessionContext = React.createContext<SessionContextProps>(undefined);
SessionContext.displayName = 'SessionContext';

const SessionProvider: React.FC = props => {
  const { children } = props;
  const { search, pushUrl } = useQueryParams();

  const [sessionState, setSessionState] = useState<SessionState>(
    initialSessionState,
  );

  const [userState, setUserState] = useState<UserState>(initialSessionValue);

  const getSession = async () => {
    try {
      const session = await userService.getSession();
      const { data: sessionData } = session;

      setSessionState({
        isSessionExpire: false,
        session: sessionData,
      });

      const { user, company: companies } = sessionData;
      const {
        username = '',
        firstname = '',
        lastname = '',
        phone = '',
        _id: userId,
      } = user;

      const company = companies?.[0];
      const { _id: companyId, name: companyName } = company;
      const project = company?.project?.[0];
      const { _id: projectId, name: projectName, role: roles } = project;
      const { permissions = [], _id: roleId, name: roleName } = roles?.[0];

      setUserState({
        userId,
        username,
        firstname,
        lastname,
        phone,
        companyId,
        companyName,
        projectId,
        projectName,
        roleId,
        roleName,
        permissions,
      });
    } catch (error) {
      setSessionState(initialSessionState);
      setUserState(initialSessionValue);
      await userService.logout();
      pushUrl('/login', search);
    }
  };

  useEffect(() => {
    // getSession();
  }, []);

  const sessionContextValues = {
    isSessionExpire: sessionState.isSessionExpire,
    session: sessionState.session,
    ...userState,
  } as SessionContextProps;

  return (
    <SessionContext.Provider value={sessionContextValues}>
      {children}
    </SessionContext.Provider>
  );
};

const useSession = () => {
  const context = React.useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must use under SessionProvider');
  }
  return context;
};

export { SessionProvider, useSession };
